import {
  ProductElementOverrideTarget,
  type ProductElementOverrides,
} from '@shared/models/project.model';
import {
  ProjectChangeType,
  type ProjectChange,
  type ProjectChangeMap,
} from './update-engine.models';

export type ProductElementTransportOverrideChange =
  ProjectChangeMap[ProjectChangeType.ProductElementTransportOverride];

export type ProductElementWasteOverrideChange =
  ProjectChangeMap[ProjectChangeType.ProductElementWasteOverride];

export type ProductElementCarbonLevelOverrideChange =
  ProjectChangeMap[ProjectChangeType.ProductElementCarbonLevelOverride];

/** Prefix for per-target proposal overlap slots (transport, waste). */
const PRODUCT_ELEMENT_OVERRIDE_TARGET_SLOT_PREFIX =
  'productElement.override.target';

/**
 * Proposal overlap slot for one {@link ProductElementOverrideTarget}.
 */
export const productElementOverrideOverlapSlot = (
  target: ProductElementOverrideTarget,
): string => `${PRODUCT_ELEMENT_OVERRIDE_TARGET_SLOT_PREFIX}:${target}`;

const CARBON_LEVEL_OVERLAP_SLOT = `${ProjectChangeType.ProductElementCarbonLevelOverride}:level`;

const PRODUCT_ELEMENT_EPD_OVERLAP_SLOT = `${ProjectChangeType.ProductElementEpd}:identity`;

const PRODUCT_ELEMENT_PRODUCT_ID_OVERLAP_SLOT = `${ProjectChangeType.ProductElementProductId}:identity`;

/** Overlap slot for {@link ProjectChangeType.ProductElementCarbonLevelOverride}. */
export const productElementCarbonLevelOverlapSlot = (): string =>
  CARBON_LEVEL_OVERLAP_SLOT;

/** Overlap slot for {@link ProjectChangeType.ProductElementEpd}. */
export const productElementEpdOverlapSlot = (): string =>
  PRODUCT_ELEMENT_EPD_OVERLAP_SLOT;

/** Overlap slot for {@link ProjectChangeType.ProductElementProductId}. */
export const productElementProductIdOverlapSlot = (): string =>
  PRODUCT_ELEMENT_PRODUCT_ID_OVERLAP_SLOT;

/**
 * Builds a transport km override change (`undefined` clears).
 */
export const createProductElementTransportOverrideChange = (
  elementId: string,
  value: number | undefined,
): ProductElementTransportOverrideChange => ({
  type: ProjectChangeType.ProductElementTransportOverride,
  elementId,
  value,
});

/**
 * Builds a waste percent override change (`undefined` clears).
 */
export const createProductElementWasteOverrideChange = (
  elementId: string,
  value: number | undefined,
): ProductElementWasteOverrideChange => ({
  type: ProjectChangeType.ProductElementWasteOverride,
  elementId,
  value,
});

/**
 * Builds a market-percentile carbon/cost pair (`undefined` clears both overrides).
 */
export const createProductElementCarbonLevelOverrideChange = (
  elementId: string,
  percentile: number | undefined,
): ProductElementCarbonLevelOverrideChange => ({
  type: ProjectChangeType.ProductElementCarbonLevelOverride,
  elementId,
  percentile,
});

/**
 * Partial {@link ProductElementOverrides} patch for a granular transport change.
 */
export const productElementTransportChangeToOverridePatch = (
  change: ProductElementTransportOverrideChange,
): Partial<ProductElementOverrides> => {
  if (change.value === undefined) {
    return { [ProductElementOverrideTarget.TRANSPORT_KM]: undefined };
  }
  return {
    [ProductElementOverrideTarget.TRANSPORT_KM]: {
      applicationScope: 'all',
      override: { type: 'replace', value: change.value },
    },
  };
};

/**
 * Partial {@link ProductElementOverrides} patch for a granular waste change.
 */
export const productElementWasteChangeToOverridePatch = (
  change: ProductElementWasteOverrideChange,
): Partial<ProductElementOverrides> => {
  if (change.value === undefined) {
    return { [ProductElementOverrideTarget.WASTE_PERCENT]: undefined };
  }
  return {
    [ProductElementOverrideTarget.WASTE_PERCENT]: {
      applicationScope: 'all',
      override: { type: 'replace', value: change.value },
    },
  };
};

/**
 * Partial {@link ProductElementOverrides} patch for a granular carbon-level change.
 */
export const productElementCarbonLevelChangeToOverridePatch = (
  change: ProductElementCarbonLevelOverrideChange,
): Partial<ProductElementOverrides> => {
  if (change.percentile === undefined) {
    return {
      [ProductElementOverrideTarget.CO2E_A1_A3]: undefined,
      [ProductElementOverrideTarget.SEK_A1_A3]: undefined,
    };
  }
  return {
    [ProductElementOverrideTarget.CO2E_A1_A3]: {
      applicationScope: 'genericProduct',
      override: {
        type: 'marketPercentileMultiplier',
        value: change.percentile,
      },
    },
    [ProductElementOverrideTarget.SEK_A1_A3]: {
      applicationScope: 'genericProduct',
      override: {
        type: 'marketPercentileMultiplier',
        value: 100 - change.percentile,
      },
    },
  };
};

/**
 * Whether the change is a granular product override proposal change.
 */
export const isProductElementOverrideChange = (
  change: ProjectChange,
): change is
  | ProductElementTransportOverrideChange
  | ProductElementWasteOverrideChange
  | ProductElementCarbonLevelOverrideChange => {
  switch (change.type) {
    case ProjectChangeType.ProductElementTransportOverride:
    case ProjectChangeType.ProductElementWasteOverride:
    case ProjectChangeType.ProductElementCarbonLevelOverride:
      return true;
    default:
      return false;
  }
};

/**
 * Overlap slot keys used by proposal subtract/prune/validation.
 */
export const getProductElementOverrideChangeOverlapSlots = (
  change: ProjectChange,
): string[] => {
  switch (change.type) {
    case ProjectChangeType.ProductElementTransportOverride:
      return [
        productElementOverrideOverlapSlot(
          ProductElementOverrideTarget.TRANSPORT_KM,
        ),
      ];
    case ProjectChangeType.ProductElementWasteOverride:
      return [
        productElementOverrideOverlapSlot(
          ProductElementOverrideTarget.WASTE_PERCENT,
        ),
      ];
    case ProjectChangeType.ProductElementCarbonLevelOverride:
      return [productElementCarbonLevelOverlapSlot()];
    case ProjectChangeType.ProductElementEpd:
      return [productElementEpdOverlapSlot()];
    case ProjectChangeType.ProductElementProductId:
      return [productElementProductIdOverlapSlot()];
    default:
      return [];
  }
};
